import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api, type ArtefactDetail, type Sidebar } from '../lib/api.ts'
import { slugForDepartment } from '../lib/departments.ts'
import { recordToolUse } from '../lib/recents.ts'
import { Breadcrumbs } from './Breadcrumbs.tsx'
import { ToolForm } from './ToolForm.tsx'
import { DocumentView } from './DocumentView.tsx'
import { DraftBanner } from './DraftBanner.tsx'

type Phase = 'idle' | 'streaming' | 'done'

// Client-side counterpart to server/draft.ts's withDraftNotice. Every other
// document surface (DocumentPage, KnowledgeTestPage, MeetingGuidePage, all via
// RenderedDoc) gets its markdown from a deterministic route that already ran
// it through withDraftNotice server-side. /api/generate is the one route that
// streams a model-authored body and never does — see server/generate.ts — so
// a bespoke tool's exported/on-screen document would otherwise carry no
// notice at all. This can't import server/draft.ts directly (src and server
// are separate TypeScript projects — tsconfig.app.json vs
// tsconfig.server.json), so the text and blockquote presentation are
// duplicated here to match; server/draft.test.ts and DraftBanner.test.tsx
// each pin their own copy so a drift between them fails loudly.
const DRAFT_NOTICE = 'Draft content, pending Astute review. Not for operational use.'
function withClientDraftNotice(markdown: string): string {
  return `> **${DRAFT_NOTICE}**\n\n${markdown}`
}

export function ToolPage() {
  const { id = '' } = useParams<{ id: string }>()
  const [tool, setTool] = useState<ArtefactDetail | null>(null)
  const [phase, setPhase] = useState<Phase>('idle')
  const [doc, setDoc] = useState('')
  const [meta, setMeta] = useState<{ sidebar: Sidebar; disclaimer: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Smooth typewriter: network deltas accumulate in `targetRef`; a rAF loop
  // reveals `shownRef` chars toward the target at a self-pacing rate — fast when
  // it's behind, easing as it catches up — so uneven network bursts render as a
  // steady, buttery character stream instead of chunks popping in.
  const targetRef = useRef('')
  const shownRef = useRef(0)
  const doneRef = useRef(false)
  const rafRef = useRef<number | null>(null)

  // Sticky form: flows at its natural top position, then pins to the vertical
  // centre of the viewport once you scroll. The sticky `top` = the offset that
  // centres the measured form; clamped so it never tucks under the topbar.
  const dockRef = useRef<HTMLDivElement>(null)
  const [stickyTop, setStickyTop] = useState(72)

  function resetOutput() {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    targetRef.current = ''
    shownRef.current = 0
    doneRef.current = false
    setDoc('')
    setMeta(null)
  }

  useEffect(() => {
    setTool(null)
    setError(null)
    setPhase('idle')
    resetOutput()
    api
      .tool(id)
      .then((t) => {
        setTool(t)
        // Only after a successful load — a broken id shouldn't enter history.
        recordToolUse({ ...t, isDraft: t.review_status !== 'approved' })
      })
      .catch(() => setError('Could not load this tool. Please try again.'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Clean up any pending frame on unmount.
  useEffect(() => () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
  }, [])

  // Centre the sticky form in the viewport; recompute on load and on resize.
  useEffect(() => {
    function recompute() {
      const h = dockRef.current?.getBoundingClientRect().height ?? 0
      setStickyTop(Math.max(72, Math.round((window.innerHeight - h) / 2)))
    }
    recompute()
    window.addEventListener('resize', recompute)
    return () => window.removeEventListener('resize', recompute)
  }, [tool])

  // Reveal a little more of the buffer each frame, then keep the loop alive
  // until every received character is shown (and the stream has ended).
  function tick() {
    const target = targetRef.current
    if (shownRef.current < target.length) {
      const remaining = target.length - shownRef.current
      // Reveal ~1/6 of what's left per frame (min 3 chars): quick to catch up on
      // a big backlog, easing to a gentle finish. Cap keeps a huge burst civil.
      const step = Math.min(remaining, Math.max(3, Math.ceil(remaining / 6)))
      shownRef.current += step
      setDoc(target.slice(0, shownRef.current))
    }
    if (doneRef.current && shownRef.current >= target.length) {
      rafRef.current = null
      setPhase('done')
      return
    }
    rafRef.current = requestAnimationFrame(tick)
  }

  function ensureTicking() {
    if (rafRef.current === null) rafRef.current = requestAnimationFrame(tick)
  }

  async function generate(formData: Record<string, string>) {
    setError(null)
    resetOutput()
    setPhase('streaming')
    // Respect reduced-motion: skip the typewriter, show text as it arrives.
    const smooth =
      typeof window !== 'undefined' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    try {
      await api.generateStream(id, formData, {
        onMeta: (m) => setMeta(m),
        onDelta: (text) => {
          targetRef.current += text
          if (smooth) ensureTicking()
          else setDoc(targetRef.current)
        },
        onError: () => {
          if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
          rafRef.current = null
          setError('Generation failed. Please try again.')
          setPhase('idle')
        },
        onDone: () => {
          doneRef.current = true
          if (smooth) ensureTicking()
          else {
            setDoc(targetRef.current)
            setPhase('done')
          }
        },
      })
    } catch {
      setError('Generation failed. Please try again.')
      setPhase('idle')
    }
  }

  const streaming = phase === 'streaming'
  const hasOutput = phase !== 'idle'
  // Fail safe, matching RenderedDoc: anything other than the exact 'approved'
  // string is treated as draft.
  const isDraftTool = tool ? tool.review_status !== 'approved' : false

  return (
    <main className="tool">
      {!tool ? (
        <p className="tool__lead">{error ?? 'Loading…'}</p>
      ) : (
        <>
          {/* First on the page, above the trail — the warning is read before
              anything it applies to. Matches RenderedDoc. */}
          {isDraftTool && <DraftBanner />}

          {/* Replaces both the old "← Departments" link and the static
              `tool.breadcrumb` eyebrow, which said the same thing twice and
              linked neither level. */}
          <Breadcrumbs
            items={[
              { label: 'Departments', to: '/' },
              { label: tool.department, to: `/departments/${slugForDepartment(tool.department)}` },
              { label: tool.title },
            ]}
          />

          <div className="tool__intro">
            <h1 className="tool__title">{tool.title}</h1>
            {tool.intro && <p className="tool__lead">{tool.intro}</p>}
          </div>

          {error && <p className="notice notice--error">{error}</p>}

          <div className="tool__grid">
            <div className="form-dock" ref={dockRef} style={{ top: stickyTop }}>
              <ToolForm schema={tool.formSchema} submitting={streaming} onSubmit={generate} />
            </div>

            {hasOutput ? (
              <DocumentView
                title={tool.title}
                meta={tool.department}
                content={isDraftTool ? withClientDraftNotice(doc) : doc}
                disclaimer={meta?.disclaimer ?? ''}
                sidebar={meta?.sidebar ?? null}
                streaming={streaming}
                isDraft={isDraftTool}
              />
            ) : (
              <section className="panel sheet">
                <div className="sheet__meta">
                  <span>{tool.department}</span>
                </div>
                <div className="sheet__placeholder">
                  <p>Complete the form to generate a guide.</p>
                </div>
              </section>
            )}
          </div>
        </>
      )}
    </main>
  )
}
